import { LitElement, css, html, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { shared } from '../styles/shared.js';
import { LocaleController } from '../i18n/locale-controller.js';
import { t } from '../i18n/t.js';
import { pickEquation, pickGateChoices, type Equation } from '../parent-gate/equations.js';
import './digit-tile.js';

/**
 * Modal arithmetic challenge that guards locale switching, progress reset,
 * and per-skill level overrides. A tap on a wrong digit silently dismisses
 * the modal — toddlers don't get fail feedback. A correct tap fires
 * `gate-success`.
 */
@customElement('parent-gate')
export class ParentGate extends LitElement {
  static override styles = [
    shared,
    css`
      .equation {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: var(--type-xxl);
        margin: var(--space-4) 0 var(--space-6);
        text-align: center;
      }

      .tiles {
        display: flex;
        gap: var(--space-4);
        justify-content: center;
        flex-wrap: wrap;
      }

      .label {
        text-align: center;
      }

      @media (max-height: 500px) {
        .equation {
          font-size: var(--type-xl);
          margin: var(--space-2) 0 var(--space-3);
        }

        .tiles {
          gap: var(--space-2);
        }
      }
    `,
  ];

  @property({ type: Boolean, reflect: true }) open = false;

  @state() private equation: Equation | null = null;
  @state() private tiles: number[] = [];

  private locale = new LocaleController(this);

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('open') && this.open && this.equation === null) {
      this.refresh();
    } else if (changed.has('open') && !this.open) {
      this.equation = null;
      this.tiles = [];
    }
  }

  private refresh(): void {
    const eq = pickEquation();
    this.equation = eq;
    this.tiles = pickGateChoices(eq.answer);
  }

  private onTileTap(value: number): void {
    if (this.equation === null) return;
    if (value === this.equation.answer) {
      this.dispatchEvent(new CustomEvent('gate-success', { bubbles: true, composed: true }));
      this.open = false;
    } else {
      this.dispatchEvent(new CustomEvent('gate-cancel', { bubbles: true, composed: true }));
      this.open = false;
    }
  }

  private onBackdropClick(): void {
    this.dispatchEvent(new CustomEvent('gate-cancel', { bubbles: true, composed: true }));
    this.open = false;
  }

  override render() {
    if (!this.open || this.equation === null) return html``;
    const loc = this.locale.locale;
    return html`<div class="modal" data-testid="parent-gate">
      <div class="modal__backdrop" @click=${this.onBackdropClick}></div>
      <div class="modal__panel">
        <p class="text-label label">${t(loc, 'gate.prompt')}</p>
        <p class="text-body label">${t(loc, 'gate.equation_label')}</p>
        <div class="equation" data-testid="gate-equation">
          ${this.equation.a} + ${this.equation.b} = ?
        </div>
        <div class="tiles">
          ${this.tiles.map(
            (v) =>
              html`<digit-tile
                variant="digit"
                .value=${v}
                data-testid="gate-tile"
                @tile-tap=${(e: CustomEvent<{ value: number | null }>) =>
                  this.onTileTap(e.detail.value!)}
              ></digit-tile>`,
          )}
        </div>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'parent-gate': ParentGate;
  }
}
